import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formattedDateTime } from '../../../utils/dateUtils';
import { isDefined } from '../../../utils/variables';
import { useDialog } from '../../hooks/use-dialog';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import { ArtifactData, ProjectArtifactsContext } from '../../state/project-artifacts-state';
import { TimelineWithIndex } from '../../state/project-timeline-state';
import { Button } from '../button/button';
import { DateTimeInput } from '../date-time-input/date-time-input';
import { Dialog } from '../dialog/dialog';
import { DropdownSuggestion } from '../dropdown-suggestion/dropdown-suggesion';
import { InfoBox } from '../info-box/info-box';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { getArtifactDisplayName, OnCreateOptions, OnDeleteOptions } from './types';

type Props = {
  timeline?: TimelineWithIndex;
  dialog: ReturnType<typeof useDialog>;
  onCreate: (options: OnCreateOptions) => void;
  onUpdate: (options: OnCreateOptions) => void;
  onDelete: (options: OnDeleteOptions) => void;
  onClose: () => void;
};

export const TimelineDialog = ({
  dialog,
  timeline,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { artifacts } = React.useContext(ProjectArtifactsContext);
  const [dateTime, setDateTime] = React.useState<Date>(
    isDefined(timeline?.collectionDate) ? new Date(timeline.collectionDate) : new Date(),
  );
  const [tiledModelId, setTiledModelId] = React.useState<string | undefined>(
    timeline?.tiledModelId ?? undefined,
  );
  const [gltftScanId, setGltftScanId] = React.useState<string | undefined>(
    timeline?.scanId ?? undefined,
  );
  const [orthoTileId, setOrthoTileId] = React.useState<string | undefined>(
    timeline?.orthoTileId ?? undefined,
  );
  const [pointCloudId, setPointCloudId] = React.useState<string | undefined>(
    timeline?.pointCloudId ?? undefined,
  );
  const [comment, setComment] = React.useState<string | undefined>(timeline?.comment ?? undefined);

  const [invalidState, setInvalidState] = React.useState<boolean | undefined>();
  // With the timeline bar, SkyView opens a timeline without data like any other
  const allowsTimelineWithoutData = useFeatureFlag('SK-7562-timeline-bar');

  const validateTimelineData = () => {
    const atLeastOneGeodataCreated =
      isDefined(tiledModelId) ||
      isDefined(gltftScanId) ||
      isDefined(orthoTileId) ||
      isDefined(pointCloudId);
    return isDefined(dateTime) && (allowsTimelineWithoutData || atLeastOneGeodataCreated);
  };

  const attemptToCreateTimeline = () => {
    if (validateTimelineData()) {
      onCreate({
        dateTime: dateTime,
        orthoTileId,
        pointCloudId,
        tiledModelId,
        gltftScanId,
        comment,
      });
      dialog.hide();
      onClose();
    } else {
      setInvalidState(true);
    }
  };

  const attemptToDeleteTimeline = () => {
    if (isDefined(timeline)) {
      onDelete({
        timelineId: timeline.id,
      });
    }
    onClose();
    dialog.hide();
  };

  const attemptToUpdateTimeline = () => {
    if (validateTimelineData()) {
      onUpdate({
        dateTime: dateTime,
        orthoTileId,
        pointCloudId,
        tiledModelId,
        gltftScanId,
        comment,
      });
      dialog.hide();
      onClose();
    } else {
      setInvalidState(true);
    }
  };

  const tileModels = useMemo(() => {
    const items: {
      id: string;
      name: string;
      type: ArtifactData['type'];
    }[] = artifacts
      .filter(
        (x) => x.status === 'COMPLETED' && (x.type === 'tiled-model' || x.type === 'legacy-scan'),
      )
      .map((x) => ({
        name: getArtifactDisplayName(x),
        id: x.id,
        type: x.type,
      }));

    return items;
  }, [artifacts]);

  const orthoTiles = useMemo(() => {
    const items: { id: string; name: string }[] = artifacts
      .filter((x) => x.status === 'COMPLETED' && x.type === 'orthotile')
      .map((x) => ({
        name: getArtifactDisplayName(x),
        id: x.id,
      }));

    return items;
  }, [artifacts]);

  const pointClouds = useMemo(() => {
    const items: { id: string; name: string }[] = artifacts
      .filter((x) => x.status === 'COMPLETED' && x.type === 'pointcloud')
      .map((x) => ({
        name: getArtifactDisplayName(x),
        id: x.id,
      }));

    return items;
  }, [artifacts]);

  return (
    <Dialog width={600} onClose={dialog.hide}>
      {{
        header: <h5>{t('menu.timeline.title', { ns: 'navigation' })}</h5>,
        content: (
          <Stack direction="column" spacing={'1em'}>
            <LabelledContainer
              text={t('timelineList.columns.collectionDate', { ns: 'components' })}
            >
              <DateTimeInput
                collapseOnEmpty={false}
                data-testid={'datepicker-from'}
                dateFormatter={formattedDateTime}
                displayTime={true}
                initialValue={dateTime}
                onDateChoosen={(date) => {
                  setDateTime(date);
                  setInvalidState(false);
                }}
              />
            </LabelledContainer>

            <LabelledContainer text={t('geodata.artifacts.3dModel', { ns: 'common' })}>
              <DropdownSuggestion
                placeholder={t('notSelected', { ns: 'common' })}
                portalToBody={true}
                suggestions={tileModels}
                value={tiledModelId ?? gltftScanId}
                width="100%"
                onClear={() => {
                  setGltftScanId(undefined);
                  setTiledModelId(undefined);
                  setInvalidState(false);
                }}
                onSuggestionSelected={(suggestion) => {
                  setGltftScanId(suggestion.type === 'legacy-scan' ? suggestion.id : undefined);
                  setTiledModelId(suggestion.type === 'tiled-model' ? suggestion.id : undefined);
                  setInvalidState(false);
                }}
              />
            </LabelledContainer>

            <LabelledContainer text={t('geodata.artifacts.pointCloud', { ns: 'common' })}>
              <DropdownSuggestion
                placeholder={t('notSelected', { ns: 'common' })}
                portalToBody={true}
                suggestions={pointClouds}
                value={pointCloudId}
                width="100%"
                onClear={() => {
                  setPointCloudId(undefined);
                  setInvalidState(false);
                }}
                onSuggestionSelected={(suggestion) => {
                  setPointCloudId(suggestion.id);
                  setInvalidState(false);
                }}
              />
            </LabelledContainer>

            <LabelledContainer text={t('geodata.artifacts.orthoPhoto', { ns: 'common' })}>
              <DropdownSuggestion
                placeholder={t('notSelected', { ns: 'common' })}
                portalToBody={true}
                suggestions={orthoTiles}
                value={orthoTileId}
                width="100%"
                onClear={() => {
                  setOrthoTileId(undefined);
                  setInvalidState(false);
                }}
                onSuggestionSelected={(suggestion) => {
                  setOrthoTileId(suggestion.id);
                  setInvalidState(false);
                }}
              />
            </LabelledContainer>

            <LabelledContainer text={t('timelineList.columns.comment', { ns: 'components' })}>
              <TextBox
                value={comment ?? ''}
                onChange={(e) => {
                  const value = e.target.value.trim().length > 0 ? e.target.value : undefined;

                  setComment(value);
                }}
              />
            </LabelledContainer>

            {invalidState && (
              <InfoBox color="red">
                {t('timelineList.dialog.missingInfo', { ns: 'components' })}
              </InfoBox>
            )}
          </Stack>
        ),
        footer: {
          right: isDefined(timeline) ? (
            <Stack direction={'row'} spacing={'0.5em'}>
              <Button
                color="primary"
                type="button"
                variant="contained"
                onClick={attemptToUpdateTimeline}
              >
                {t('update', { ns: 'common' })}
              </Button>
              <Button
                color="secondary"
                type="button"
                variant="contained"
                onClick={() => {
                  onClose();
                  dialog.hide();
                }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </Stack>
          ) : (
            <>
              <Button
                color="primary"
                type="button"
                variant="contained"
                onClick={attemptToCreateTimeline}
              >
                {t('create', { ns: 'common' })}
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  onClose();
                  dialog.hide();
                }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
          left: isDefined(timeline) && (
            <Button
              color="error"
              type="button"
              variant="contained"
              onClick={attemptToDeleteTimeline}
            >
              {t('delete', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};
